import { h } from "preact";
import htm from "htm";
import { locale, tr } from "./i18n.js";

export const html = htm.bind(h);
export const APP_NAME = "RELAYZA";
export const MARKETING_URL = "https://relayza.company";
/**
 * Inside the Android or iPhone app's full-dashboard view (both web views add "RelayzaApp/" to the
 * user agent). There the app is the one signed in, so signing out lives in the app's menu, and the
 * "Mobile app" link becomes the way back to the app's quick view, which the app intercepts. Billing is
 * never shown there: Google Play's Payments policy and App Store guideline 3.1 both let the app open a
 * subscription bought elsewhere but not sell one or point to where it is sold (docs/android-release.md,
 * docs/ios-release.md).
 */
export const IN_APP = typeof navigator !== "undefined" && /\bRelayzaApp\//.test(navigator.userAgent);
export const EARLY_ACCESS_HREF = `mailto:hello@relayza.company?subject=${encodeURIComponent("RELAYZA early access")}`;

// ── Theme (dark is the RELAYZA default; light is opt-in and remembered) ──────

const THEME_KEY = "rz.theme";
export function getTheme() {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}
export function setTheme(theme) {
  if (theme === "light") document.documentElement.dataset.theme = "light";
  else delete document.documentElement.dataset.theme;
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* private mode */
  }
}
export function applySavedTheme() {
  try {
    if (localStorage.getItem(THEME_KEY) === "light") document.documentElement.dataset.theme = "light";
  } catch {
    /* private mode */
  }
}

export class ApiError extends Error {
  constructor(status, code, message, data = {}) {
    super(message);
    this.status = status;
    this.code = code;
    /** Whatever else the server sent with the error, e.g. a suggested alternative. */
    this.data = data;
  }
}

export async function api(method, path, body) {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? `Request failed (${res.status})`, data?.error ?? {});
  return data;
}

export const get = (p) => api("GET", p);

/**
 * A file as base64, which is how uploads reach the API: the dashboard's CSRF guard admits
 * application/json only, and a multipart form is a content type a browser can post cross-site.
 */
export function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error(tr("We couldn't read that file.")));
    reader.readAsDataURL(file);
  });
}

/** "1.4 MB", for file sizes and character counts in the documents list. */
export function fmtBytes(n) {
  if (!n) return "0 KB";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
export const post = (p, b = {}) => api("POST", p, b);
export const put = (p, b) => api("PUT", p, b);
export const patch = (p, b) => api("PATCH", p, b);
export const del = (p) => api("DELETE", p, {});

// ── Router (history API) ─────────────────────────────────────────────────────

const listeners = new Set();
export function navigate(to, { replace = false } = {}) {
  if (replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  listeners.forEach((fn) => fn());
}
export function onNavigate(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
window.addEventListener("popstate", () => listeners.forEach((fn) => fn()));

/** Intercepts clicks on same-origin <a> so links don't reload the app. */
export function linkHandler(e) {
  const a = e.target.closest?.("a");
  if (!a || a.target || a.origin !== location.origin || e.metaKey || e.ctrlKey || e.button !== 0) return;
  if (a.pathname.startsWith("/api/") || a.hasAttribute("download") || a.hash) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
}

// ── Formatting ───────────────────────────────────────────────────────────────

export function fmtDateTime(iso, tz) {
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
}
export function fmtTime(iso, tz) {
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
}
export function fmtDay(iso, tz) {
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, weekday: "long", day: "numeric", month: "long" }).format(new Date(iso));
}
/** Chat day divider: "Today", "Yesterday", else the weekday and date (with the year once it is not this year). */
export function fmtChatDay(iso, tz) {
  const today = localDate(new Date(), tz);
  const day = localDate(new Date(iso), tz);
  if (day === today) return tr("Today");
  if (day === addDays(today, -1)) return tr("Yesterday");
  const year = day.slice(0, 4) === today.slice(0, 4) ? {} : { year: "numeric" };
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, weekday: "long", day: "numeric", month: "long", ...year }).format(new Date(iso));
}
export function localDate(d, tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
export function addDays(isoDate, n) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}
export function relTime(iso) {
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 60) return tr("now");
  if (s < 3600) return tr("{n}m", { n: Math.floor(s / 60) });
  if (s < 86400) return tr("{n}h", { n: Math.floor(s / 3600) });
  return tr("{n}d", { n: Math.floor(s / 86400) });
}
export const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);

/** 1234.5 ILS → "₪1,235". Whole units: these are report totals, not invoices. */
export function fmtMoney(amount, currency = "ILS") {
  try {
    return new Intl.NumberFormat(locale(), { style: "currency", currency: currency || "ILS", maximumFractionDigits: 0 }).format(amount ?? 0);
  } catch {
    return `${Math.round(amount ?? 0)} ${currency}`;
  }
}

/** [{currency, amount}] from the API → "₪12,400" or "₪12,400 + $300"; "—" when there is nothing. */
export const fmtMoneyList = (list) => (list?.length ? list.map((m) => fmtMoney(m.amount, m.currency)).join(" + ") : "—");

/** "YYYY-MM-DDTHH:mm" in the business's time zone (the format the API takes for local times). */
export function localDateTime(d, tz) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Quick due-date choices for follow-up tasks, in the business's local time. */
export function taskPresets(tz) {
  const now = new Date();
  const today = localDate(now, tz);
  return [
    { label: "In 2 hours", due: localDateTime(new Date(now.getTime() + 2 * 3600_000), tz) },
    { label: "Tomorrow 10:00", due: `${addDays(today, 1)}T10:00` },
    { label: "In 3 days", due: `${addDays(today, 3)}T10:00` },
    { label: "Next week", due: `${addDays(today, 7)}T10:00` },
  ];
}

export const LOST_REASON_LABEL = {
  price: "Price",
  timing: "Timing",
  chose_competitor: "Chose a competitor",
  not_a_fit: "Not a fit",
  no_response: "Stopped replying",
  location: "Location",
  other: "Other",
};

export const STAGES = ["new", "engaged", "qualified", "booked", "customer", "lost"];
/** Display label for a CRM stage (the stored value stays English). */
export const stageLabel = (s) => tr(s);
export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** [["09:00","13:00"]] → "09:00-13:00", the way the hours fields are edited. */
export const intervalsToText = (intervals = []) => intervals.map(([a, b]) => `${a}-${b}`).join(", ");

/** Parses "09:00-13:00, 16:00-19:00"; throws a readable error so the caller can toast it. */
export function parseIntervals(text) {
  if (!text.trim()) return [];
  return text.split(",").map((part) => {
    const m = /^\s*(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})\s*$/.exec(part);
    if (!m) throw new Error(tr('Couldn\'t read "{part}". Use 09:00-13:00, 16:00-19:00', { part: part.trim() }));
    return [m[1].padStart(5, "0"), m[2].padStart(5, "0")];
  });
}

/** { "0": [["09:00","18:00"]] } → ["09:00-18:00", "", …] by weekday, Sunday first. */
export const weeklyHoursToText = (weekly = {}) => DAYS.map((_, i) => intervalsToText(weekly[String(i)] ?? []));

export const textToWeeklyHours = (texts) => Object.fromEntries(texts.map((text, i) => [String(i), parseIntervals(text)]));

export const CHANNEL_LABEL = { whatsapp: tr("WhatsApp"), messenger: tr("Messenger"), instagram: tr("Instagram"), webchat: tr("Web chat") };
export const STATUS_LABEL = { ai: tr("AI handling"), human: tr("Needs a person"), closed: tr("Closed") };
export { tr };

